import lodash from 'lodash';
import {toJS, observable, action, runInAction} from 'mobx';
import {observer, PropTypes as MobxPropTypes} from 'mobx-react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, {Component} from 'react';
import _throttle from 'lodash/throttle';

import {TabContent, TabPane, Nav, NavItem, NavLink} from 'reactstrap';

import LoadingIcon from '../loadingIcon/LoadingIcon';
import inject from '../../hoc/injectHoc';
import {SCREEN_SIZE_MEDIUM} from '../../../constants/bootstrapConstants';
import {PLAN_DATA, NEXTLED_SIGNUP_TYPE, ILLUMINATIONDISPLAYS_SIGNUP_TYPE} from '../../../constants/subscriptionPlanConstants';
import {centsToDollars} from '../../../utils/mathHelper';

import PlanItem from './components/planItem/PlanItem';

import './subscriptionPlanTable.scss';

const UPDATE_PLAN_REASON_TRIAL = 'trial_expired';
const TRIAL_PLAN_NAME = 'Trial';
const BASIC_PLAN_NAME = 'Basic';
const NEXT_PLAN_NAME = 'NextLED';
const ILLUMINATIONDISPLAYS_PLAN_NAME = 'Illumination Displays';

/**
 * Cleans up the plan object from the server.
 *
 * @param {{}} plan
 * @returns {{}}
 */
export const cleanUpPlan = (plan) => ({
  ...plan,
  ...PLAN_DATA[plan.name],
  price: centsToDollars(plan.price),
  priceCents: plan.price,
  displayPrice: centsToDollars(plan.displayPrice),
  displayPriceCents: plan.displayPrice
});

/**
 * The SubscriptionPlanTable component.
 */
class SubscriptionPlanTable extends Component {
  /**
   * Whether the plans should be displayed in mobile view
   *
   * @type {boolean}
   */
  @observable isMobileView = false;

  /**
   * Plan that is showing when in tabbed mobile view
   *
   * @type {boolean}
   */
  @observable activePlanTab = null;

  /**
 * Determine if plan table should be in mobile view
 */
  checkWindowWidth = _throttle(() => {
    runInAction('setisMobileView', () => {
      const windowWidth = window.innerWidth;
      this.isMobileView = windowWidth < SCREEN_SIZE_MEDIUM;
    });
  // eslint-disable-next-line no-magic-numbers
  }, 500);

  /**
   * Triggered when the component first mounts to the page.
   */
  componentDidMount() {
    const {apiPlanGetAllStore} = this.props;

    apiPlanGetAllStore.refresh();

    this.checkPlansForSelected();

    // check window size immediately
    this.checkWindowWidth();
    window.addEventListener('resize', this.checkWindowWidth);
  }

  /**
   * Triggered when the component updates.
   *
   * @param {{}} prevProps
   */
  componentDidUpdate(prevProps) {
    if (this.props.selectPlanId && this.props.selectPlanId !== prevProps.selectPlanId) {
      this.checkPlansForSelected();
    }
  }

  /**
   * Clean up window events to avoid memory leaks
   */
  @action componentWillUnmount() {
    window.removeEventListener('resize', this.checkWindowWidth);
  }

  /**
   * Selects the active plan tab
   *
   * @param {number} planId
   */
  @action selectActivePlanTab(planId) {
    this.activePlanTab = planId;
  }

  /**
   * Selects the plan if the selectPlanId matches a plan.
   */
  checkPlansForSelected = () => {
    const {apiPlanGetAllStore, onPlanSelect, selectPlanId} = this.props;

    if (selectPlanId && onPlanSelect) {
      apiPlanGetAllStore.getPromise().then((plans) => {
        const matchingPlan = lodash.find(toJS(plans), {id: selectPlanId});
        if (!matchingPlan) {
          return;
        }

        this.selectActivePlanTab(matchingPlan.id);

        onPlanSelect(cleanUpPlan(matchingPlan));
      });
    }
  };

  /**
   * Parses the plans into values that are more friendly for the display table.
   *
   * @param {Array<{}>} plans
   * @returns {Array<{}>}
   */
  parsePlansForTable = (plans) => {
    return plans.map((plan) => {
      return cleanUpPlan(plan);
    });
  };

  /**
   * Get supplemental download credits
   * @param {*} signupType
   * @param {*} plans
   * @returns {number}
   */
  getSupplementalDownloadCredits = (signupType, plans) => {
    const nextPlan = lodash.find(toJS(plans), {name: NEXT_PLAN_NAME});
    const illuminationDisplaysPlan = lodash.find(toJS(plans), {name: ILLUMINATIONDISPLAYS_PLAN_NAME});

    if (signupType === NEXTLED_SIGNUP_TYPE && nextPlan) {
      return nextPlan.downloadCredits;
    }

    if (signupType === ILLUMINATIONDISPLAYS_SIGNUP_TYPE && illuminationDisplaysPlan) {
      return illuminationDisplaysPlan.downloadCredits;
    }

    return 0;
  };

  /**
   * Get Plan Download Credits. Taking into account supplemental credits if necessary.
   *
   * @param {*} plan
   * @param {*} signupType
   * @param {*} plans
   * @returns {number}
   */
  getPlanDownloadCredits = (plan, signupType, plans) => {
    if (!plan.downloadCredits) {
      return 0;
    }
    const isManufacturerPlan =
      plan.name === NEXT_PLAN_NAME || plan.name === ILLUMINATIONDISPLAYS_PLAN_NAME;

    // Manufacturer users get bonus credits on OTHER plans only
    if (signupType && !isManufacturerPlan) {
      return plan.downloadCredits + this.getSupplementalDownloadCredits(signupType, plans);
    }

    return plan.downloadCredits;
  };

  /**
   * Format Plan information for subscription plan table
   *
   * @param {*} plans
   * @returns {{}}
   */
  filterPlansForTable = (plans) => {
    const {user, fromOnboarding} = this.props;
    const signupType = lodash.get(user, 'company.manufacturerSignupType');

    const planDisplayRulesByName = {
      [BASIC_PLAN_NAME]:
      user &&
      user.requiresPlanUpdate &&
      user.planUpdateReason !== UPDATE_PLAN_REASON_TRIAL &&
      !signupType,

      [NEXT_PLAN_NAME]:
      signupType === NEXTLED_SIGNUP_TYPE && !fromOnboarding,

      [ILLUMINATIONDISPLAYS_PLAN_NAME]:
      signupType === ILLUMINATIONDISPLAYS_SIGNUP_TYPE && !fromOnboarding
    };

    const trialPlan = lodash.find(toJS(plans), {name: TRIAL_PLAN_NAME});
    const planId = lodash.get(user, 'projectContentCompany.planId');
    const hasUsedTrial = lodash.get(
      user,
      'projectContentCompany.hasUsedTrial'
    );

    return plans.reduce((validPlans, plan) => {
      const mostExpensive = plans.every(
        (p) => p.priceCents <= plan.priceCents
      );

      const isActive = planId
        ? planId === plan.id ||
        (planId === trialPlan.id && plan.name === 'Essential')
        : false;

      const planToShow = {
        ...plan,
        isActive,
        allowTrial:
        plan.allowTrial && !hasUsedTrial && !signupType,
        isRecommended: mostExpensive,
        downloadCredits: this.getPlanDownloadCredits(plan, signupType, plans)
      };

      const shouldShow =
      plan.isVisible || planDisplayRulesByName[plan.name];

      return shouldShow
        ? [...validPlans, planToShow]
        : validPlans;
    }, []);
  };

  /**
   * Renders the mobile tab view of plans.
   *
   * @returns {{}}
   */
  renderPlanMobileView = () => {
    const {apiPlanGetAllStore, onPlanSelect, disablePlanButtons} = this.props;

    const allPlans = this.parsePlansForTable(
      apiPlanGetAllStore.getFulfilled() || []
    );
    const trialPlan = lodash.find(toJS(allPlans), {name: TRIAL_PLAN_NAME});

    const plans = this.filterPlansForTable(allPlans);

    // select the last plan if it's a customer from a manufacturer, otherwise show the first plan
    const defaultActivePlanTab = plans[0].id;
    const activePlanShown = plans.find((plan) => plan.id === this.activePlanTab);
    const activeTab = activePlanShown ? this.activePlanTab : defaultActivePlanTab;

    return (
      <div className="mt-4 m-2">
        <Nav tabs className="plan-list-tabs">
          {plans.map((plan) => (
            <NavItem key={plan.id}>
              <NavLink
                className={classNames({active: activeTab === plan.id})}
                onClick={() => this.selectActivePlanTab(plan.id)} key={plan.id}
              >
                {plan.name}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
        <TabContent activeTab={activeTab} className="pt-3">
          {plans.map((plan, idx) => (<TabPane tabId={plan.id} key={plan.id}>
            <PlanItem
              key={plan.id}
              plan={plan}
              planIndex={idx}
              planCount={plans.length}
              onPlanSelect={() => onPlanSelect(plan)}
              onTrialSelect={() => onPlanSelect(trialPlan)}
              disableButtons={disablePlanButtons}
            />
          </TabPane>))}
        </TabContent>
      </div>
    );
  }

  /**
   * Renders the full plan table.
   *
   * @returns {{}}
   */
  renderPlanTable = () => {
    const {apiPlanGetAllStore, onPlanSelect, disablePlanButtons} = this.props;

    const allPlans = this.parsePlansForTable(
      apiPlanGetAllStore.getFulfilled() || []
    );
    const trialPlan = lodash.find(toJS(allPlans), {name: TRIAL_PLAN_NAME});

    const plans = this.filterPlansForTable(allPlans);

    return (
      <div className="plan-list-container mt-4">
        <div className="plan-list row no-gutters">
          {
            plans.map((plan, idx) => {
              return (
                <div
                  className="col"
                  key={plan.id}
                >
                  <PlanItem
                    plan={plan}
                    planIndex={idx}
                    planCount={plans.length}
                    onPlanSelect={() => onPlanSelect(plan)}
                    onTrialSelect={() => onPlanSelect(trialPlan)}
                    disableButtons={disablePlanButtons}
                  />
                </div>
              );
            })
          }
        </div>
      </div>
    );
  };

  /**
   * Renders the component.
   *
   * @returns {{}}
   */
  render() {
    const {apiPlanGetAllStore} = this.props;

    return apiPlanGetAllStore.case({
      pre: () => (<LoadingIcon />),
      pending: () => (<LoadingIcon />),
      rejected: () => (
        <div className="subscription-plan-table">
          <div className="alert alert-warning">
            The plan data was not able to be loaded. Please refresh the page to try again.
          </div>
        </div>
      ),
      fulfilled: () => (
        <>{this.isMobileView ? this.renderPlanMobileView() : this.renderPlanTable()}</>
      ),
    });
  }
}

SubscriptionPlanTable.propTypes = {
  apiCompanySignGetAllStore: MobxPropTypes.observableObject,
  apiPlanGetAllStore: MobxPropTypes.observableObject,
  disablePlanButtons: PropTypes.bool,
  fromOnboarding: PropTypes.bool,
  onPlanSelect: PropTypes.func,
  selectPlanId: PropTypes.number,
  user: PropTypes.object
};

SubscriptionPlanTable.wrappedComponent = {};
SubscriptionPlanTable.wrappedComponent.propTypes = {
  apiCompanySignGetAllStore: MobxPropTypes.observableObject.isRequired,
  apiPlanGetAllStore: MobxPropTypes.observableObject.isRequired,
};

export default inject(SubscriptionPlanTable)(
  observer(SubscriptionPlanTable)
);
